import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  Dispatch,
} from "react";

type ProductCreatedRange = {
  startDate: string;
  endDate: string;
};

type State = {
  searchIndex: string;
  resultCount: number;
  isResultTabView?: boolean;
  productCreatedRange: ProductCreatedRange | null;
};

type Action =
  | { type: "SET_RESULT_COUNT"; payload: number }
  | { type: "ADD_TO_RESULT_COUNT"; payload: number }
  | { type: "SET_SEARCH_INDEX"; payload: string }
  | { type: "SET_IS_RESULT_TAB_VIEW"; payload: boolean }
  | { type: "SET_PRODUCT_CREATED_RANGE"; payload: ProductCreatedRange | null };

const SearchContext = createContext<{
  state: State;
  dispatch: Dispatch<Action>;
}>({
  state: { searchIndex: "", resultCount: 0, productCreatedRange: null },
  dispatch: () => null,
});

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_RESULT_COUNT":
      return { ...state, resultCount: action.payload };
    case "ADD_TO_RESULT_COUNT":
      return { ...state, resultCount: state.resultCount + action.payload };
    case "SET_SEARCH_INDEX":
      return { ...state, searchIndex: action.payload };
    case "SET_IS_RESULT_TAB_VIEW":
      return { ...state, isResultTabView: action.payload };
    case "SET_PRODUCT_CREATED_RANGE":
      return { ...state, productCreatedRange: action.payload };
    default:
      return state;
  }
};

type SearchContextProviderProps = {
  children: ReactNode;
  searchIndex: string;
};

export const SearchContextProvider = ({
  children,
  searchIndex,
}: SearchContextProviderProps) => {
  const initialState: State = {
    searchIndex,
    resultCount: 0,
    productCreatedRange: null,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <SearchContext.Provider value={{ state, dispatch }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => useContext(SearchContext);

// For setting the search index
export const useSearchIndex = () => {
  const { dispatch, state } = useSearchContext();

  const setSearchIndex = (index: string) => {
    dispatch({ type: "SET_SEARCH_INDEX", payload: index });
  };

  return {
    searchIndex: state.searchIndex,
    setSearchIndex,
  };
};

// For setting isResultTabView
export const useIsResultTabView = () => {
  const { dispatch, state } = useSearchContext();

  const setIsResultTabView = (isResultTabView: boolean) => {
    dispatch({ type: "SET_IS_RESULT_TAB_VIEW", payload: isResultTabView });
  };

  return {
    isResultTabView: state.isResultTabView,
    setIsResultTabView,
  };
};

// productCreatedRange: Only set via explicit user action (toggle in RecentlyAddedDropdown).
// Never initialize from monarch config—monarch provides labels/range options; user must opt in.
export const useProductCreatedRange = () => {
  const { dispatch, state } = useSearchContext();

  const setProductCreatedRange = (range: ProductCreatedRange | null) => {
    dispatch({ type: "SET_PRODUCT_CREATED_RANGE", payload: range });
  };

  const clearProductCreatedRange = () => {
    dispatch({ type: "SET_PRODUCT_CREATED_RANGE", payload: null });
  };

  return {
    productCreatedRange: state.productCreatedRange,
    setProductCreatedRange,
    clearProductCreatedRange,
  };
};
